/**
 * Web stand-in for `expo-crypto` (redirected by the NormalModuleReplacementPlugin
 * in webpack.config.js — every `expo-crypto` request resolves to this file on web).
 *
 * Why: the Expo 55.0.32+ toolchain (@expo/cli >= 55.0.37) resolves expo-crypto to
 * its TypeScript SOURCE (src/Crypto.ts) for web builds, bypassing both package
 * entry aliases and the normal build/ resolution. The published src/aes/ files do
 * not resolve ('./ExpoCryptoAES', './web-utils'), so webpack ships a throwing
 * stub and the app crashes at boot with "Cannot find module './ExpoCryptoAES'".
 *
 * Implements the subset of the API the web app uses (PKCE / OAuth code flow —
 * `useIntegrationOAuthCodeFlow` + expo-auth-session's PKCE) with browser
 * WebCrypto, matching the published build/Crypto.js semantics:
 * - CryptoDigestAlgorithm values are the WebCrypto algorithm names ("SHA-256"),
 * - digestStringAsync returns hex / base64 (ArrayBuffer for anything else),
 * - getRandomBytes is synchronous, like the native API.
 * MD2/MD4/MD5 are unavailable (WebCrypto has no MD*) — they only work on native.
 */

const encoder = new TextEncoder();

export const CryptoDigestAlgorithm = Object.freeze({
  SHA1: 'SHA-1',
  SHA256: 'SHA-256',
  SHA384: 'SHA-384',
  SHA512: 'SHA-512',
  MD2: 'MD2',
  MD4: 'MD4',
  MD5: 'MD5',
});

export const CryptoEncoding = Object.freeze({
  HEX: 'hex',
  BASE64: 'base64',
});

const SUPPORTED_ALGORITHMS = new Set(['SHA-1', 'SHA-256', 'SHA-384', 'SHA-512']);

function assertByteCount(value, methodName) {
  if (
    typeof value !== 'number' ||
    isNaN(value) ||
    Math.floor(value) < 0 ||
    Math.floor(value) > 1024
  ) {
    throw new TypeError(
      `expo-crypto (web): ${methodName}(${value}) expected a number between 0 and 1024`
    );
  }
}

function bytesToHex(bytes) {
  let hex = '';
  for (let i = 0; i < bytes.length; i += 1) {
    hex += bytes[i].toString(16).padStart(2, '0');
  }
  return hex;
}

function bytesToBase64(bytes) {
  let binary = '';
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

/** @returns {Promise<ArrayBuffer>} — matches the native `digest` return type. */
export async function digest(algorithm, data) {
  if (!SUPPORTED_ALGORITHMS.has(algorithm)) {
    throw new Error(
      `expo-crypto (web): unsupported algorithm "${algorithm}" (WebCrypto has no MD*)`
    );
  }
  return crypto.subtle.digest(algorithm, data);
}

/**
 * Digests a UTF-8 string. Defaults to HEX, like the native API.
 * @returns {Promise<string | ArrayBuffer>}
 */
export async function digestStringAsync(
  algorithm,
  data,
  options = { encoding: CryptoEncoding.HEX }
) {
  if (typeof data !== 'string') {
    throw new TypeError('expo-crypto (web): digestStringAsync(data) expected a string');
  }
  const encoding = options?.encoding ?? CryptoEncoding.HEX;
  const buffer = await digest(algorithm, encoder.encode(data));
  const bytes = new Uint8Array(buffer);
  if (encoding === CryptoEncoding.BASE64) return bytesToBase64(bytes);
  if (encoding === CryptoEncoding.HEX) return bytesToHex(bytes);
  return buffer;
}

/** Fills a typed array with secure random bytes (sync, like the native API). */
export function getRandomValues(typedArray) {
  crypto.getRandomValues(typedArray);
  return typedArray;
}

/** @returns {Uint8Array} */
export function getRandomBytes(byteCount) {
  assertByteCount(byteCount, 'getRandomBytes');
  return crypto.getRandomValues(new Uint8Array(Math.floor(byteCount)));
}

/** @returns {Promise<Uint8Array>} */
export async function getRandomBytesAsync(byteCount) {
  assertByteCount(byteCount, 'getRandomBytesAsync');
  return crypto.getRandomValues(new Uint8Array(Math.floor(byteCount)));
}

/** RFC 4122 v4 UUID via WebCrypto (crypto.randomUUID when available). */
export function randomUUID() {
  if (typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = bytesToHex(bytes);
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(
    16,
    20
  )}-${hex.slice(20)}`;
}
